import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { queryKeys } from '@src/utils/queryKeys';
import { useQueryClient } from '@tanstack/react-query';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';
import { useQuestionAndAnswerStore } from '@src/stores';

import {
  EAnswerStatus,
  EAnswerType,
  EUserType,
  IQuestion,
  IQuestionAnswer,
  IQuestionAndAnswerResponse,
  EQuestionAndAnswerSockets,
} from '@src/types';
import { toast } from 'react-toastify';

interface IProps {
  question: IQuestion;
  isReplyingPrivately: boolean;
  handleOnClose: (posted: boolean) => void;
}

const useAnswerQuestion = ({ question, isReplyingPrivately, handleOnClose }: IProps) => {
  const [answerText, setAnswerText] = useState('');

  const { currentPeerType } = useRoomContext();
  const queryClient = useQueryClient();

  const { setIsAnswerBoxOpened, answerToEdit, setAnswerToEdit, updateQuestion } = useQuestionAndAnswerStore(
    useShallow((state) => ({
      setIsAnswerBoxOpened: state.setIsAnswerBoxOpened,
      answerToEdit: state.answerToEdit,
      setAnswerToEdit: state.setAnswerToEdit,
      updateQuestion: state.updateQuestion,
    }))
  );

  const isPostAnswerDisabled = useMemo(() => !answerText.trim().length, [answerText]);

  const isEditAnswerDisabled = useMemo(
    () => answerText.trim() === answerToEdit?.text.trim(),
    [answerText, answerToEdit]
  );

  const resetStates = useCallback(() => {
    setAnswerText('');
    setAnswerToEdit(null);
    setIsAnswerBoxOpened(false);
  }, [setAnswerToEdit, setIsAnswerBoxOpened]);

  const handleAnswerQuestionClick = useCallback(async () => {
    if ([EUserType.HOST, EUserType.CO_HOST].includes(currentPeerType as EUserType)) {
      // Unassign the question when closing the answer box
      try {
        await SocketService.emit(EQuestionAndAnswerSockets.UNASSIGN_QUESTION, {
          questionId: question.id,
        });
      } catch (error) {
        console.error('Failed to unassign question:', error);
      }
    }

    resetStates();
  }, [resetStates, question.id, currentPeerType]);

  useEffect(() => {
    if (answerToEdit) {
      setAnswerText(answerToEdit.text);
    }
  }, [answerToEdit]);

  const handleChangeAnswer = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    setAnswerText(text);
  }, []);

  const handlePostAnswer = useCallback(async () => {
    if (answerToEdit) {
      try {
        const response: IQuestionAndAnswerResponse = await SocketService.request(
          EQuestionAndAnswerSockets.EDIT_ANSWER,
          {
            questionId: question.id,
            text: answerText,
            answerType: answerToEdit.answerType,
          }
        );

        if (!response?.success) {
          console.error('Failed to edit answer:', response?.message);
          return;
        }

        updateQuestion(question.id, response.question);

        resetStates();
        return;
      } catch (error) {
        console.error('Failed to edit answer:', error);
        return;
      }
    }

    // For thread replies, inherit the privacy type from the initial answer
    const isThreadReply = question?.answer?.status === EAnswerStatus.ANSWERED;
    const threadReplyAnswerType = isThreadReply ? question.answer?.answerType : undefined;

    const answerData: IQuestionAnswer = {
      text: answerText,
      timestamp: new Date().toString() || '',
      status: EAnswerStatus.ANSWERED,
      answerType: isThreadReply
        ? threadReplyAnswerType
        : isReplyingPrivately
          ? EAnswerType.PRIVATE
          : EAnswerType.PUBLIC,
      edited: false,
    };
    if (isThreadReply) {
      const result = await SocketService.request(EQuestionAndAnswerSockets.REPLY_TO_ANSWER, {
        questionId: question.id,
        text: answerData.text,
        answerType: answerData.answerType,
      });
      if (result?.success) {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.webinar.questionReply(question.id)],
        });
      } else {
        toast.error(result?.message || 'Failed to send reply');
      }
    } else {
      SocketService.request(EQuestionAndAnswerSockets.ANSWER_QUESTION, {
        questionId: question.id,
        text: answerData.text,
        answerType: answerData.answerType,
      }).then(() => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.webinar.questions],
        });
      });
    }
    handleOnClose(true);
  }, [
    answerToEdit,
    answerText,
    isReplyingPrivately,
    question?.answer?.status,
    question?.answer?.answerType,
    question.id,
    handleOnClose,
    updateQuestion,
    resetStates,
    queryClient,
  ]);

  const handleAnswerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (!isPostAnswerDisabled) {
          handlePostAnswer();
        }
      }
    },
    [handlePostAnswer, isPostAnswerDisabled]
  );

  return {
    answerText,
    currentPeerType,
    isPostAnswerDisabled,
    isEditAnswerDisabled,
    answerToEdit,
    handlePostAnswer,
    handleChangeAnswer,
    handleAnswerKeyDown,
    handleAnswerQuestionClick,
  };
};

export default useAnswerQuestion;
